import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import type { TransactionDetailSection } from '@/components/transactions/TransactionDetailModal';
import { TransactionDetailModal } from '@/components/transactions/TransactionDetailModal';
import { TransactionLogCard } from '@/components/transactions/TransactionLogCard';
import { useTransactionLog } from '@/hooks/useTransactionLog';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { getErrorMessage } from '@/utils/httpClient';
import { getCurrentFiscalYear, getFiscalYearStartDate } from '@/utils/fiscalYear';
import { buildMemberTransactionLogUrl } from './transactionHistoryUtils';
import { usePersonLabels } from '@/hooks/usePersonLabels';

type HistoryRange = 'current-fy' | 'custom' | 'full-history';

export const TransactionHistoryTab: React.FC = () => {
  const navigate = useNavigate();
  const { currentCustomer } = useIemFrontDoorStore();
  const person = usePersonLabels('front_window');
  const [range, setRange] = useState<HistoryRange>('current-fy');
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [customTo, setCustomTo] = useState<Dayjs | null>(dayjs());
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [initialDetailSection, setInitialDetailSection] = useState<
    TransactionDetailSection | undefined
  >(undefined);

  const dateTimeFormatter = useMemo(
    () => new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    [],
  );

  const formatDateTime = useCallback(
    (value: string | null | undefined) => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return dateTimeFormatter.format(date);
    },
    [dateTimeFormatter],
  );

  const fyRange = useMemo(() => {
    const currentFiscalYear = getCurrentFiscalYear();
    return {
      from: dayjs(getFiscalYearStartDate(currentFiscalYear)),
      to: dayjs(),
      label: `Current FY (${currentFiscalYear})`,
    };
  }, []);

  const hasCustomFrom = Boolean(customFrom?.isValid());
  const hasCustomTo = Boolean(customTo?.isValid());
  const customRangeIncomplete = range === 'custom' && (!hasCustomFrom || !hasCustomTo);
  const customRangeInvalid =
    range === 'custom' &&
    hasCustomFrom &&
    hasCustomTo &&
    (customFrom as Dayjs).isAfter(customTo as Dayjs, 'day');

  const fromForApi = useMemo(() => {
    if (range === 'full-history') return undefined;
    if (range === 'custom') {
      return hasCustomFrom ? customFrom?.format('YYYY-MM-DD') : undefined;
    }
    return fyRange.from.format('YYYY-MM-DD');
  }, [customFrom, fyRange.from, hasCustomFrom, range]);

  const toForApi = useMemo(() => {
    if (range === 'full-history') return undefined;
    if (range === 'custom') {
      return hasCustomTo ? customTo?.format('YYYY-MM-DD') : undefined;
    }
    return fyRange.to.format('YYYY-MM-DD');
  }, [customTo, fyRange.to, hasCustomTo, range]);

  const canQueryHistory =
    Boolean(currentCustomer?.id) && !customRangeIncomplete && !customRangeInvalid;

  const {
    data: transactions = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useTransactionLog({
    memberId: currentCustomer?.id,
    from: fromForApi,
    to: toForApi,
    enabled: canQueryHistory,
  });

  const rangeLabel = useMemo(() => {
    if (range === 'full-history') {
      return 'Full History';
    }
    if (range === 'custom') {
      const from = fromForApi ?? '—';
      const to = toForApi ?? '—';
      return `Custom: ${from} to ${to}`;
    }
    return fyRange.label;
  }, [fromForApi, fyRange.label, range, toForApi]);

  useEffect(() => {
    if (!selectedTransactionId) return;
    const exists = transactions.some((tx) => tx.id === selectedTransactionId);
    if (!exists) {
      setSelectedTransactionId(null);
      setInitialDetailSection(undefined);
    }
  }, [selectedTransactionId, transactions]);

  const handleOpenTransactionLog = () => {
    if (!currentCustomer?.id || !canQueryHistory) {
      return;
    }
    navigate(
      buildMemberTransactionLogUrl({
        memberId: currentCustomer.id,
        memberName: currentCustomer.display_name,
        from: fromForApi,
        to: toForApi,
      }),
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Review transaction history for the selected {person.lower} by date range or full history.
      </Typography>

      {!currentCustomer ? (
        <Alert severity="info">Select {person.article} {person.lower} to view transaction history.</Alert>
      ) : (
        <Paper elevation={1} sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                select
                label="Period"
                size="small"
                value={range}
                sx={{ minWidth: { xs: '100%', md: 220 } }}
                onChange={(event) => setRange(event.target.value as HistoryRange)}
              >
                <MenuItem value="current-fy">{fyRange.label}</MenuItem>
                <MenuItem value="custom">Custom range</MenuItem>
                <MenuItem value="full-history">Full history</MenuItem>
              </TextField>

              {range === 'custom' && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <DatePicker
                      label="Date From"
                      value={customFrom}
                      onChange={(value) => setCustomFrom(value?.isValid() ? value : null)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: { xs: '100%', md: 180 } },
                        },
                      }}
                    />
                    <DatePicker
                      label="Date To"
                      value={customTo}
                      onChange={(value) => setCustomTo(value?.isValid() ? value : null)}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { minWidth: { xs: '100%', md: 180 } },
                        },
                      }}
                    />
                  </Stack>
                </LocalizationProvider>
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Chip
                color="primary"
                variant="outlined"
                label={`${person.label}: ${currentCustomer.display_name ?? currentCustomer.id}`}
              />
              <Chip variant="outlined" label={rangeLabel} />
            </Stack>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                endIcon={<OpenInNew />}
                onClick={handleOpenTransactionLog}
                disabled={!canQueryHistory}
              >
                Open in Transaction Log
              </Button>
              <Button
                variant="text"
                onClick={() => void refetch()}
                disabled={!canQueryHistory || isFetching}
              >
                Refresh
              </Button>
            </Box>

            {customRangeIncomplete && (
              <Alert severity="info">Choose both start and end dates for a custom range.</Alert>
            )}
            {customRangeInvalid && (
              <Alert severity="error">Date From cannot be after Date To.</Alert>
            )}

            {canQueryHistory && isLoading ? (
              <Box
                sx={{
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Loading transactions…
                </Typography>
              </Box>
            ) : canQueryHistory && isError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              >
                {getErrorMessage(error)}
              </Alert>
            ) : canQueryHistory && transactions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No transactions found for this member and period.
              </Typography>
            ) : (
              <Stack spacing={1.25}>
                {transactions.map((transaction) => (
                  <TransactionLogCard
                    key={transaction.id}
                    transaction={transaction}
                    formatDateTime={formatDateTime}
                    onOpen={(transactionId, options) => {
                      setSelectedTransactionId(transactionId);
                      setInitialDetailSection(options?.initialSection);
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      <TransactionDetailModal
        open={Boolean(selectedTransactionId)}
        transactionId={selectedTransactionId}
        initialSection={initialDetailSection}
        onClose={() => {
          setSelectedTransactionId(null);
          setInitialDetailSection(undefined);
        }}
        formatDateTime={formatDateTime}
      />
    </Stack>
  );
};
